var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { setInfoMessage } from '@popup/multichain/actions/message.actions';
import { setAccounts } from '@popup/steem/actions/account.actions';
import { loadActiveAccount } from '@popup/steem/actions/active-account.actions';
import AccountUtils from '@popup/steem/utils/account.utils';
import React, { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable, } from 'react-beautiful-dnd';
import Select from 'react-dropdown-select';
import { connect } from 'react-redux';
import { SVGIcons } from 'src/common-ui/icons.enum';
import { PreloadedImage } from 'src/common-ui/preloaded-image/preloaded-image.component';
import { SelectAccountSectionItemComponent } from 'src/common-ui/select-account-section/select-account-section-item.component';
import { SVGIcon } from 'src/common-ui/svg-icon/svg-icon.component';
import ButtonComponent from 'src/common-ui/button/button.component';
// Valori di default
const DEFAULT_CONFIG = {
    steem_api: "https://api.steemit.com",
    hive_api: "https://api.openhive.network",
    blurt_api: "https://rpc.blurt.blog",
    steem_color: "#90ee90",
    hive_color: "#ffff00",
    blurt_color: "#FF6600"
};
// Variabile globale per la Promise (tipizzata)
let configPromise = null;
/**
 * Funzione principale per ottenere un valore di configurazione.
 * Carica la config da server se non è già stata caricata, altrimenti usa i default.
 * @param key La chiave da cercare (es. 'steem_api', 'hive_color')
 * @returns Il valore della chiave o il default se non trovato
 */
export const getDefVal = (key) => __awaiter(void 0, void 0, void 0, function* () {
    // Se non c'è una promise in corso, creiamone una
    let keyl = key.toLowerCase();
    if (!configPromise) {
        configPromise = (() => __awaiter(void 0, void 0, void 0, function* () {
            try {
                // Nota: fetch non supporta nativamente 'timeout' in RequestInit.
                // Usiamo AbortController per il timeout.
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 3000);
                const response = yield fetch('https://timeapp.foundation/defval.php', {
                    method: 'GET',
                    signal: controller.signal // Passa il segnale di abort
                });
                clearTimeout(timeoutId);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const json = yield response.json();
                if (json.ok && json.data) {
                    // console.log('✅ Config loaded from server');
                    return json.data;
                }
            }
            catch (error) {
                console.warn('⚠️ Server unreachable or invalid response, using defaults:', error);
            }
            // Fallback ai default
            return DEFAULT_CONFIG;
        }))();
    }
    // Attendiamo che la promise si risolva
    const config = yield configPromise;
    // Restituiamo il valore richiesto con fallback sicuro
    // Usiamo 'as any' per bypassare il controllo di indice se la chiave non è esplicitamente nel tipo,
    // ma dato che abbiamo [key: string]: string nell'interfaccia, dovrebbe funzionare direttamente.
    return config[keyl] || DEFAULT_CONFIG[keyl] || '';
});
const SelectAccountSection = ({ background, fullSize, accounts, setAccounts, activeAccount, loadActiveAccount, setInfoMessage, isOnMain = false, }) => {
    var _a;
    const [isOpened, setIsOpened] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [accountToDelete, setAccountToDelete] = useState(null);
    const defaultOptions = [];
    const [options, setOptions] = useState(defaultOptions);
    const [selectedLocalAccount, setSelectedLocalAccount] = useState(((_a = accounts[0]) === null || _a === void 0 ? void 0 : _a.name) || '');
    const [avatarCache, setAvatarCache] = useState({});
    const [avatarUrl, setAvatarUrl] = useState('');
    // ====================== CARICA TUTTI GLI AVATAR ALL'AVVIO ======================
    useEffect(() => {
        setOptions(accounts.map((account) => ({
            label: account.name,
            value: account.name,
        })));
        setSelectedLocalAccount(activeAccount.name);
        // Pre-carica avatar per TUTTI gli account
        accounts.forEach((account) => {
            if (account.name) {
                fetchAvatar(account.name);
            }
        });
    }, [accounts, activeAccount]);
    useEffect(() => {
        if (selectedLocalAccount) {
            setAvatarUrl(avatarCache[selectedLocalAccount] || '');
        }
    }, [selectedLocalAccount, avatarCache]);
    useEffect(() => {
        const newOptions = accounts.map((account) => {
            const avatar = avatarCache[account.name] || '';
            return {
                label: account.name,
                value: account.name,
                avatar: avatar, // ← Aggiunto
            };
        });
        setOptions(newOptions);
        setSelectedLocalAccount(activeAccount.name);
    }, [accounts, activeAccount, avatarCache]);
    // ====================== LONG PRESS LOGIC ======================
    const [pressTimer, setPressTimer] = useState(null);
    const startLongPress = (accountName) => {
        const timer = setTimeout(() => {
            setAccountToDelete(accountName);
            setShowDeleteConfirm(true);
        }, 1000);
        setPressTimer(timer);
    };
    const cancelLongPress = () => {
        if (pressTimer) {
            clearTimeout(pressTimer);
            setPressTimer(null);
        }
    };
    // ====================== FETCH AVATAR ======================
    const fetchAvatar = (username) => __awaiter(void 0, void 0, void 0, function* () {
        var _b, _c;
        if (avatarCache[username] !== undefined)
            return; // già caricato o tentato
        const chains = ['steem', 'hive', 'blurt'];
        for (const chain of chains) {
            try {
                const rpcUrl = yield getDefVal(`${chain}_api`);
                if (!rpcUrl)
                    continue;
                const payload = {
                    jsonrpc: '2.0',
                    method: 'condenser_api.get_accounts',
                    params: [[username]],
                    id: 1
                };
                const response = yield fetch(rpcUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
                const data = yield response.json();
                if (data.result && data.result[0]) {
                    const account = data.result[0];
                    let profileImage = '';
                    if (account.posting_json_metadata) {
                        try {
                            const meta = JSON.parse(account.posting_json_metadata);
                            profileImage = ((_b = meta === null || meta === void 0 ? void 0 : meta.profile) === null || _b === void 0 ? void 0 : _b.profile_image) || '';
                        }
                        catch (e) { }
                    }
                    if (!profileImage && account.json_metadata) {
                        try {
                            const meta = JSON.parse(account.json_metadata);
                            profileImage = ((_c = meta === null || meta === void 0 ? void 0 : meta.profile) === null || _c === void 0 ? void 0 : _c.profile_image) || '';
                        }
                        catch (e) { }
                    }
                    if (profileImage) {
                        profileImage = profileImage.replace(/\\+/g, '');
                        setAvatarCache(prev => (Object.assign(Object.assign({}, prev), { [username]: profileImage })));
                        return;
                    }
                }
            }
            catch (e) {
            }
        }
        // Default avatar con prime 3 lettere
        const defaultAvatar = generateDefaultAvatar(username);
        setAvatarCache(prev => (Object.assign(Object.assign({}, prev), { [username]: defaultAvatar })));
    });
    // ====================== DEFAULT AVATAR ======================
    const generateDefaultAvatar = (username) => {
        const initials = username.slice(0, 3).toUpperCase();
        const vibrantColors = [
            '#ff79c6', '#50fa7b', '#f1fa8c', '#bd93f9', '#ff5555',
            '#8be9fd', '#ffb86c', '#ff9ff3', '#a3ff7e', '#ffe66d',
            '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeead',
            '#ff9f1c', '#e63946', '#a8dadc', '#457b9d', '#f4a261',
            '#e76f51', '#2a9d8f', '#264653', '#e9c46a', '#f4a261',
            '#ef476f', '#06d6a0', '#118ab2', '#073b4c', '#ffd166',
            '#ff006e', '#8338ec', '#3a86ff', '#06ffa5', '#ffbe0b'
        ];
        const hash = username.split('').reduce((acc, char) => {
            return char.charCodeAt(0) + ((acc << 5) - acc);
        }, 0);
        const colorIndex = Math.abs(hash) % vibrantColors.length;
        const textColor = vibrantColors[colorIndex];
        return `https://ui-avatars.com/api/?name=${initials}&background=0d1117&color=${encodeURIComponent(textColor)}&size=150&bold=true&font-size=0.50`;
    };
    // ====================== LONG PRESS ======================
    const confirmDelete = () => {
        if (!accountToDelete)
            return;
        const newAccounts = accounts.filter((acc) => acc.name !== accountToDelete);
        setAccounts(newAccounts);
        if (activeAccount.name === accountToDelete && newAccounts.length > 0) {
            loadActiveAccount(newAccounts[0]);
        }
        setShowDeleteConfirm(false);
        setAccountToDelete(null);
        setInfoMessage(`Account @${accountToDelete} deleted successfully`);
    };
    const cancelDelete = () => {
        setShowDeleteConfirm(false);
        setAccountToDelete(null);
    };
    const handleItemClicked = (accountName) => {
        const itemClicked = accounts.find((account) => account.name === accountName);
        loadActiveAccount(itemClicked);
        handleClickOnSelector();
    };
    const onDragEnd = (result) => {
        if (!result.destination)
            return;
        const list = Array.from(options);
        const [removed] = list.splice(result.source.index, 1);
        list.splice(result.destination.index, 0, removed);
        setOptions(list);
        setAccounts(AccountUtils.reorderAccounts(accounts, result.source.index, result.destination.index));
    };
    const handleClickOnSelector = () => {
        setIsOpened(!isOpened);
    };
    // ====================== RENDERERS ======================
    const customLabelRender = () => (React.createElement("div", { className: `selected-account-panel ${fullSize ? 'fullsize' : ''}`, onClick: handleClickOnSelector },
        React.createElement(PreloadedImage, { className: "user-picture", src: avatarUrl || '/assets/images/menu/accounts.svg', alt: '/assets/images/menu/accounts.svg', placeholder: '/assets/images/menu/accounts.svg' }),
        React.createElement("div", { className: "selected-account-name", "data-testid": "selected-account-name" }, selectedLocalAccount)));
    const customHandleRenderer = () => (React.createElement(SVGIcon, { className: "custom-select-handle", icon: isOpened ? SVGIcons.SELECT_ARROW_UP : SVGIcons.SELECT_ARROW_DOWN, onClick: handleClickOnSelector }));
    // ====================== DROPDOWN RENDERER CORRETTO ======================
    // ====================== DROPDOWN RENDERER - FORCE HEIGHT ======================
    const customDropdownRenderer = ({ props, methods }) => {
        return (React.createElement("div", { className: "custom-select-dropdown", style: {
                height: '600px !important',
                maxHeight: '600px !important',
                overflowY: 'auto',
                overflowX: 'hidden',
                padding: '4px 0',
                marginTop: '2px',
                display: 'block'
            } },
            React.createElement(DragDropContext, { onDragEnd: onDragEnd },
                React.createElement(Droppable, { droppableId: "droppable-account", type: "account", isDropDisabled: !isOnMain }, (provided) => (React.createElement("div", Object.assign({}, provided.droppableProps, { ref: provided.innerRef, style: {
                        padding: '0',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                    } }),
                    props.options.map((option, index) => (React.createElement(Draggable, { key: option.value, draggableId: option.value, isDragDisabled: !isOnMain, index: index }, (provided) => (React.createElement("div", Object.assign({ ref: provided.innerRef }, provided.draggableProps, provided.dragHandleProps, { style: Object.assign(Object.assign({}, provided.draggableProps.style), { marginBottom: '0px', flexShrink: 0 }), onTouchStart: () => startLongPress(option.value), onTouchEnd: cancelLongPress, onTouchCancel: cancelLongPress, onMouseDown: () => startLongPress(option.value), onMouseUp: cancelLongPress, onMouseLeave: cancelLongPress }),
                        React.createElement(SelectAccountSectionItemComponent, { key: `option-${option.value}`, isLast: props.options.length === index + 1, item: option, selectedAccount: selectedLocalAccount, handleItemClicked: handleItemClicked, isOnMain: isOnMain, dragHandle: provided.dragHandleProps, closeDropdown: () => methods.dropDown('close'), setInfoMessage: setInfoMessage })))))),
                    provided.placeholder))))));
    };
    return (React.createElement(React.Fragment, null,
        selectedLocalAccount && options && (React.createElement("div", { className: `select-account-section ${fullSize ? 'fullsize' : ''} ${isOpened ? 'opened' : 'closed'}` },
            React.createElement(Select, { keepOpen: true, values: [selectedLocalAccount], options: options, onChange: () => undefined, contentRenderer: customLabelRender, className: `select-account-select ${background ? background : ''}`, dropdownHandleRenderer: customHandleRenderer, dropdownRenderer: customDropdownRenderer }),
            React.createElement("div", { className: `overlay ${isOpened ? 'opened' : 'closed'}`, onClick: () => setIsOpened(false) }))),
        showDeleteConfirm && accountToDelete && (React.createElement("div", { style: {
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.85)', zIndex: 10000,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
            } },
            React.createElement("div", { style: {
                    background: '#161b22', color: '#e6edf3', padding: '24px',
                    borderRadius: '12px', maxWidth: '380px', width: '90%', textAlign: 'center'
                } },
                React.createElement("h3", null, "Delete Account"),
                React.createElement("p", { style: { margin: '16px 0', lineHeight: '1.5' } },
                    "Are you sure you want to permanently delete",
                    React.createElement("br", null),
                    React.createElement("strong", null,
                        "@",
                        accountToDelete),
                    "?"),
                React.createElement("p", { style: { color: '#ff6b6b', fontSize: '0.9em' } }, "This action cannot be undone."),
                React.createElement("div", { style: { display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '20px' } },
                    React.createElement(ButtonComponent, { label: "Cancel", onClick: cancelDelete }),
                    React.createElement(ButtonComponent, { label: "Delete Account", onClick: confirmDelete })))))));
};
const mapStateToProps = (state) => ({
    accounts: state.steem.accounts,
    activeAccount: state.steem.activeAccount,
});
const connector = connect(mapStateToProps, {
    loadActiveAccount,
    setInfoMessage,
    setAccounts,
});
export const SelectAccountSectionComponent = connector(SelectAccountSection);
